/**
 * 统一文章数据管理
 * 确保归档、首页、文章详情都使用同一份数据
 * 添加 localStorage 持久化支持
 * 新增：草稿/已发布文章管理功能
 */
// 引入mock数据
import { defaultPostsData} from '../api/mockData.js';

// 从 localStorage 加载数据，如果没有则使用mock的默认数据
const loadPostsData = () => {
  const saved = localStorage.getItem('blog_posts_data');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (error) {
      console.error('解析文章数据失败:', error);
      return getDefaultPostsData();
    }
  }
  return getDefaultPostsData();
};

// 获取默认文章数据（从mockData引入）
const getDefaultPostsData = () => {
  return defaultPostsData;
};

// 保存数据到 localStorage
const savePostsData = (data) => {
  try {
    localStorage.setItem('blog_posts_data', JSON.stringify(data));
  } catch (error) {
    console.error('保存文章数据失败:', error);
  }
};

// 初始化文章数据
let postsData = loadPostsData();

// 自动保存数据
const saveData = () => {
  savePostsData(postsData);
};

// ========== 原有核心函数（兼容修改） ==========
// 获取所有已发布的文章（保留原有逻辑，不影响首页/归档）
export const getAllPosts = () => {
  return postsData.filter(post => post.published);
};

// 获取指定ID的文章（兼容草稿：支持查询草稿，新增参数 includeDrafts）
export const getPostById = (id, includeDrafts = false) => {
  const postId = parseInt(id);
  const post = postsData.find(post => post.id === postId);
  // 如果允许查草稿，直接返回；否则只返回已发布的
  return (includeDrafts || (post && post.published)) ? post : null;
};

// 获取最新文章
export const getLatestPosts = (limit = 10) => {
  return getAllPosts()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, limit);
};

// 获取精选文章
export const getFeaturedPosts = () => {
  return getAllPosts().filter(post => post.featured);
};

// 根据分类获取文章
export const getPostsByCategory = (category) => {
  if (category === '全部') {
    return getAllPosts();
  }
  return getAllPosts().filter(post => post.category === category);
};

// 搜索文章
export const searchPosts = (keyword) => {
  if (!keyword.trim()) return getAllPosts();
  
  const searchTerm = keyword.toLowerCase();
  return getAllPosts().filter(post =>
    post.title.toLowerCase().includes(searchTerm) ||
    post.excerpt.toLowerCase().includes(searchTerm) ||
    post.content.toLowerCase().includes(searchTerm) ||
    post.tags.some(tag => tag.toLowerCase().includes(searchTerm)) ||
    post.category.toLowerCase().includes(searchTerm) ||
    post.author.name.toLowerCase().includes(searchTerm)
  );
};

// 添加新文章（新增：支持创建草稿）
export const addNewPost = (postData) => {
  const newId = postsData.length > 0 ? Math.max(...postsData.map(p => p.id)) + 1 : 1;
  // 补充 status 字段（和mockData对齐）
  const status = postData.published ? 'published' : 'draft';
  
  // 获取当前登录用户ID
  const currentUserId = getCurrentUserId();
  
  const newPost = {
    id: newId,
    ...postData,
    status, // 新增：文章状态
    views: postData.views || 0,
    likes: postData.likes || 0,
    comments: postData.comments || 0,
    featured: postData.featured || false,
    published: postData.published !== undefined ? postData.published : true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    // 确保新文章归属当前登录用户
    author: {
      ...postData.author,
      id: currentUserId
    }
  };
  
  postsData.unshift(newPost); // 添加到开头
  saveData(); // 保存到 localStorage
  return newPost;
};

// 更新文章（兼容：同步更新 status 字段）
export const updatePost = (id, updates) => {
  const index = postsData.findIndex(post => post.id === id);
  if (index !== -1) {
    // 如果更新了 published，同步更新 status
    if (updates.published !== undefined) {
      updates.status = updates.published ? 'published' : 'draft';
    }
    postsData[index] = {
      ...postsData[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    saveData(); // 保存到 localStorage
    return postsData[index];
  }
  return null;
};

// 删除文章（优化：从软删除改为硬删除，适配个人中心删除需求）
export const deletePost = (id) => {
  const index = postsData.findIndex(post => post.id === id);
  if (index !== -1) {
    // 硬删除：直接从数组中移除（原有软删除只改published，不适合草稿管理）
    postsData.splice(index, 1);
    saveData(); // 保存到 localStorage
    return true;
  }
  return false;
};

// 增加阅读量
export const incrementViewCount = (id) => {
  const post = getPostById(id);
  if (post) {
    post.views = (post.views || 0) + 1;
    saveData(); // 保存到 localStorage
    window.dispatchEvent(new CustomEvent('blogDataChanged', { detail: { postId: id } }));
    return post.views;
  }
  return 0;
};

// 点赞文章
export const likePost = (id) => {
  const post = getPostById(id);
  if (post) {
    post.likes = (post.likes || 0) + 1;
    saveData(); // 保存到 localStorage
    window.dispatchEvent(new CustomEvent('blogDataChanged', { detail: { postId: id } }));
    return post.likes;
  }
  return 0;
};

// 取消点赞文章
export const unlikePost = (id) => {
  const post = getPostById(id);
  if (post && post.likes > 0) {
    post.likes = Math.max(0, (post.likes || 0) - 1);
    saveData(); // 保存到 localStorage
    window.dispatchEvent(new CustomEvent('blogDataChanged', { detail: { postId: id } }));
    return post.likes;
  }
  return 0;
};

// 添加评论计数
export const addCommentCount = (id) => {
  const post = getPostById(id);
  if (post) {
    post.comments = (post.comments || 0) + 1;
    saveData(); // 保存到 localStorage
    window.dispatchEvent(new CustomEvent('blogDataChanged', { detail: { postId: id } }));
    return post.comments;
  }
  return 0;
};

// 归档统计相关函数
export const getArchiveStats = () => {
  const posts = getAllPosts();
  const total = posts.length;
  
  // 按年份分组
  const byYear = {};
  posts.forEach(post => {
    const year = new Date(post.createdAt).getFullYear();
    if (!byYear[year]) {
      byYear[year] = {
        year,
        count: 0,
        months: {}
      };
    }
    byYear[year].count++;
    
    // 按月份统计
    const month = new Date(post.createdAt).getMonth() + 1; // 1-12
    if (!byYear[year].months[month]) {
      byYear[year].months[month] = 0;
    }
    byYear[year].months[month]++;
  });
  
  // 转换为数组并排序
  const years = Object.values(byYear)
    .sort((a, b) => b.year - a.year)
    .map(yearData => ({
      ...yearData,
      months: Object.entries(yearData.months)
        .map(([month, count]) => ({
          month: parseInt(month),
          monthName: getMonthName(month),
          count
        }))
        .sort((a, b) => b.month - a.month)
    }));
  
  // 热门标签统计
  const tagStats = {};
  posts.forEach(post => {
    post.tags.forEach(tag => {
      if (!tagStats[tag]) {
        tagStats[tag] = 0;
      }
      tagStats[tag]++;
    });
  });
  
  const popularTags = Object.entries(tagStats)
    .map(([name, count]) => ({
      name,
      count,
      size: count > 10 ? 'lg' : count > 5 ? 'md' : 'sm'
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20);
  
  return {
    total,
    years,
    popularTags,
    byCategory: getPostsByCategoryStats(),
    monthlyStats: getMonthlyStats()
  };
};

// 获取月份名称
const getMonthName = (month) => {
  const months = [
    '一月', '二月', '三月', '四月', '五月', '六月',
    '七月', '八月', '九月', '十月', '十一月', '十二月'
  ];
  return months[month - 1] || '';
};

// 按分类统计
export const getPostsByCategoryStats = () => {
  const posts = getAllPosts();
  const stats = {};
  
  posts.forEach(post => {
    if (!stats[post.category]) {
      stats[post.category] = 0;
    }
    stats[post.category]++;
  });
  
  return Object.entries(stats)
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count);
};

// 月度统计
const getMonthlyStats = () => {
  const posts = getAllPosts();
  const monthly = {};
  
  posts.forEach(post => {
    const date = new Date(post.createdAt);
    const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    
    if (!monthly[yearMonth]) {
      monthly[yearMonth] = {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        monthName: getMonthName(date.getMonth() + 1),
        count: 0,
        views: 0,
        likes: 0
      };
    }
    
    monthly[yearMonth].count++;
    monthly[yearMonth].views += post.views || 0;
    monthly[yearMonth].likes += post.likes || 0;
  });
  
  return Object.values(monthly)
    .sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    })
    .slice(0, 12); // 最近12个月
};

// 获取作者统计
export const getAuthorStats = () => {
  const posts = getAllPosts();
  const authorStats = {};
  
  posts.forEach(post => {
    const authorId = post.author.id;
    if (!authorStats[authorId]) {
      authorStats[authorId] = {
        ...post.author,
        posts: 0,
        views: 0,
        likes: 0,
        comments: 0
      };
    }
    
    authorStats[authorId].posts++;
    authorStats[authorId].views += post.views || 0;
    authorStats[authorId].likes += post.likes || 0;
    authorStats[authorId].comments += post.comments || 0;
  });
  
  return Object.values(authorStats)
    .sort((a, b) => b.posts - a.posts);
};

// 获取热门文章
export const getPopularPosts = (limit = 10) => {
  return getAllPosts()
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, limit);
};

// 获取相关文章
export const getRelatedPosts = (postId, limit = 3) => {
  const currentPost = getPostById(postId);
  if (!currentPost) return [];
  
  const allPosts = getAllPosts().filter(post => post.id !== postId);
  
  // 按相关度评分
  const scoredPosts = allPosts.map(post => {
    let score = 0;
    
    // 相同分类加分
    if (post.category === currentPost.category) score += 3;
    
    // 相同标签加分
    const commonTags = post.tags.filter(tag => 
      currentPost.tags.includes(tag)
    ).length;
    score += commonTags * 2;
    
    // 相同作者加分
    if (post.author.id === currentPost.author.id) score += 2;
    
    // 浏览量加分
    score += Math.min(post.views / 100, 5);
    
    return { post, score };
  });
  
  return scoredPosts
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(item => item.post);
};

// 数据管理工具函数
export const resetToDefaultData = () => {
  postsData = getDefaultPostsData();
  saveData();
  return postsData;
};

export const exportData = () => {
  return JSON.stringify(postsData, null, 2);
};

export const importData = (jsonData) => {
  try {
    const newData = JSON.parse(jsonData);
    postsData = newData;
    saveData();
    return true;
  } catch (error) {
    console.error('导入数据失败:', error);
    return false;
  }
};

// ========== 新增：个人中心核心函数（草稿/已发布管理） ==========

/**
 * 获取当前登录用户ID
 * @returns {number} 当前用户ID
 */
export const getCurrentUserId = () => {
  const userId = localStorage.getItem('currentUserId') || '1'; // 默认为1（咖啡喵）
  return parseInt(userId);
};

/**
 * 获取当前登录用户信息
 * @returns {object} 用户信息
 */
export const getCurrentUser = () => {
  const userId = getCurrentUserId();
  const userName = localStorage.getItem('currentUserName') || '咖啡喵';
  
  // 模拟不同用户的信息
  const userMap = {
    1: {
      id: 1,
      name: '咖啡喵',
      nickname: '咖啡喵',
      avatar: '🐱',
      bio: '热爱生活，喜欢记录生活中的美好瞬间。相信每一天都有值得感恩的小确幸。',
      role: '生活博主'
    },
    2: {
      id: 2,
      name: '代码狗',
      nickname: '代码狗',
      avatar: '🐶',
      bio: '技术极客，热爱开源和新技术。负责维护博客的技术架构和用户体验优化。',
      role: '前端工程师'
    },
    3: {
      id: 3,
      name: '书虫君',
      nickname: '书虫君',
      avatar: '📚',
      bio: '热爱阅读，喜欢分享读书心得和感悟。相信文字有力量，书籍是通往世界的窗口。',
      role: '文学爱好者'
    },
    4: {
      id: 4,
      name: '旅行猫',
      nickname: '旅行猫',
      avatar: '✈️',
      bio: '热爱旅行，喜欢用镜头记录世界的美好。已走过30+国家，分享旅行故事和实用攻略。',
      role: '旅行摄影师'
    }
  };
  
  // 如果是新注册的用户（ID>=5），返回自定义信息
  if (userId >= 5) {
    return {
      id: userId,
      name: userName,
      nickname: userName,
      avatar: '👤',
      bio: `嗨，我是${userName}，很高兴加入小狗日志！`,
      role: '新用户'
    };
  }
  
  return userMap[userId] || userMap[1];
};

/**
 * 获取当前用户的文章（支持按状态筛选）
 * @param {number} userId - 用户ID（默认使用当前登录用户）
 * @param {string} status - 筛选状态：''(全部) / 'published'(已发布) / 'draft'(草稿)
 * @returns {Array} 筛选后的文章列表（按更新时间倒序）
 */
export const getMyPosts = (userId = getCurrentUserId(), status = '') => {
  // 转换为数字ID（兼容字符串/数字）
  const currentId = parseInt(userId);
  
  // 1. 筛选当前用户的所有文章（包括草稿）
  let userPosts = postsData.filter(post => parseInt(post.author.id) === currentId);
  
  // 2. 按状态筛选
  if (status === 'published') {
    userPosts = userPosts.filter(post => post.published);
  } else if (status === 'draft') {
    userPosts = userPosts.filter(post => !post.published);
  }
  
  // 3. 按更新时间倒序排列
  return userPosts.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
};

/**
 * 保存草稿
 * @param {object} postData - 草稿数据
 * @returns {object} 保存后的草稿对象
 */
export const saveDraft = (postData) => {
  // 获取当前登录用户ID
  const currentUserId = getCurrentUserId();
  
  const newPost = {
    ...postData,
    status: 'draft',       // 标记为草稿状态
    published: false,      // 未发布
    views: 0,              // 草稿无阅读量
    likes: 0,              // 草稿无点赞
    comments: 0,           // 草稿无评论
    featured: false,       // 草稿不精选
    createdAt: postData.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    id: postData.id || Date.now(), // 新草稿用时间戳做唯一ID
    // 确保草稿归属当前登录用户
    author: {
      ...postData.author,
      id: currentUserId
    }
  };
  
  // 更新或新增草稿
  const index = postsData.findIndex(p => p.id === newPost.id);
  if (index > -1) {
    postsData[index] = newPost;
  } else {
    postsData.unshift(newPost); // 新增草稿放到开头
  }
  
  saveData(); // 持久化到localStorage
  // 触发数据变更事件
  window.dispatchEvent(new CustomEvent('blogDataChanged', { detail: { postId: newPost.id } }));
  return newPost;
};

/**
 * 发布草稿（将草稿转为已发布文章）
 * @param {number} postId - 草稿ID
 * @returns {object|null} 发布后的文章对象（失败返回null）
 */
export const publishPost = (postId) => {
  const post = getPostById(postId, true); // 允许查询草稿
  
  // 验证：只能发布自己的草稿
  const currentUserId = getCurrentUserId();
  if (post && !post.published && parseInt(post.author.id) === currentUserId) {
    // 更新发布状态
    const updatedPost = updatePost(postId, {
      published: true,
      status: 'published',
      date: new Date().toLocaleDateString() // 更新发布日期
    });
    return updatedPost;
  }
  return null;
};

/**
 * 获取当前用户的统计数据
 * @param {number} userId - 用户ID（默认使用当前登录用户）
 * @returns {object} 统计数据：{posts: 已发布数, drafts: 草稿数, views: 总阅读量, likes: 总获赞数}
 */
export const getUserStats = (userId = getCurrentUserId()) => {
  const userPosts = getMyPosts(userId);
  return {
    posts: userPosts.filter(p => p.published).length, // 已发布数
    drafts: userPosts.filter(p => !p.published).length, // 草稿数
    views: userPosts.reduce((sum, post) => sum + (post.views || 0), 0), // 总阅读量
    likes: userPosts.reduce((sum, post) => sum + (post.likes || 0), 0) // 总获赞数
  };
};